import { excludeNullOrUndefined } from 'checkout/utils'
import { sum } from 'lib/array/arrayUtils'
import { createSelector } from 'reselect'
import getInsuranceItemsView from 'checkout/selectors/view/itemViews/getInsuranceItemsView'

export const getInsuranceTotals = createSelector(
  (state: App.State) => getInsuranceItemsView(state),
  (viewWithStatus): App.WithDataStatus<App.Checkout.ItemViewTotals> => {
    const definedItems = viewWithStatus.data.filter(excludeNullOrUndefined)

    const itemsWithMobileAppDiscount = definedItems.filter((item) => item.totals.mobileAppDiscount?.amount)
    const mobileAppDiscountAmount = itemsWithMobileAppDiscount.length ? sum(itemsWithMobileAppDiscount, ({ totals }) => totals.mobileAppDiscount?.amount ?? 0) : 0
    const mobileAppDiscountPercentage = itemsWithMobileAppDiscount.length ? sum(itemsWithMobileAppDiscount, ({ totals }) => totals.mobileAppDiscount?.percentage ?? 0) / itemsWithMobileAppDiscount.length : 0

    // Per-item memberPrice is already 0 for anything this buyer isn't genuinely eligible for
    // (see getInsuranceItemsView) - a per-item value check is the correct source of truth here,
    // not a single flag-based boolean, which would otherwise still sum the full price for a
    // flight CFMR item whenever the legacy insurance-discount flag happens to be off.
    // A post-purchase update whose re-quoted policy is cheaper carries a negative price. That
    // difference is refunded to the customer's original payment method by svc-order once the
    // update is confirmed, so it is never netted against what the customer pays here.
    const totals: App.Checkout.ItemViewTotals = {
      price: sum(definedItems, itemView => Math.max(
        0,
        (itemView.totals.memberPrice ?? 0) > 0 ? itemView.totals.memberPrice : (itemView.totals.price ?? 0),
      )),
      memberPrice: 0,
      // `value`, `paidPrice` and `newPrice` are summed as the item views report them. The views
      // already floor `newPrice` at 0 for a cheaper re-quote (see getInsuranceItemsView): that
      // refund is stated on its own instead of lowering the "New total" row. `price` is what
      // gets charged, so it is floored here too.
      value: sum(definedItems, itemView => (itemView.totals.value ?? 0)),
      paidPrice: sum(definedItems, itemView => (itemView.totals.paidPrice ?? 0)),
      newPrice: sum(definedItems, itemView => (itemView.totals.newPrice ?? 0)),
      surcharge: 0,
      taxesAndFees: 0,
      ...(mobileAppDiscountAmount > 0 && {
        mobileAppDiscount: {
          amount: mobileAppDiscountAmount,
          percentage: mobileAppDiscountPercentage,
        },
      }),
      memberValue: 0,
      extraGuestSurcharge: 0,
    }

    return {
      hasRequiredData: viewWithStatus.hasRequiredData,
      data: totals,
    }
  },
)
